/**
 * The arithmetic behind the time wheel, kept apart from the column that draws it so
 * that it can be checked without a browser.
 *
 * The column is a scroll container five numbers tall with the chosen one in a band
 * across the middle. Every length here is one of those two facts.
 */

/** One number's worth of column, which is also the height of the band. */
export const ITEM = 34;

/** Five of them: the chosen number, and two either side. */
export const ROWS = 5;

/** The height of the window the column rolls behind. */
export const WINDOW = ITEM * ROWS;

/** Where the band starts and ends within that window. */
export const BAND: readonly [number, number] = [ITEM * 2, ITEM * 3];

/** How far the column has scrolled when `index` is the number in the band. */
export const scrollFor = (index: number): number => index * ITEM;

/** Which number the column is standing on, to the fraction, at `scrolled`. */
export const standingAt = (scrolled: number): number => scrolled / ITEM;

/**
 * Where to put the window's gradient under number `i` so that it lines up with the
 * band rather than with the number.
 *
 * The two numbers' worth of nothing above the first one count towards where that is:
 * the column's content starts two items before number 0.
 */
export const bandOffset = (i: number, scrolled: number): number =>
  scrolled - (i + 2) * ITEM;

/**
 * The rows of that gradient a number covers — which is to say how much of it is
 * inside the band and how much is not. `[68, 102]` is all of it in.
 */
export function coversBand(i: number, scrolled: number): [number, number] {
  const top = -bandOffset(i, scrolled);
  return [top, top + ITEM];
}

/**
 * The type a number `gap` numbers from the band is set in: the design's three steps
 * — 26/800 in the band, 19/600 beside it, 15/600 beyond — and everything in between
 * them.
 *
 * Between, and not merely at, the steps: a number rolling towards the band has to
 * grow into it. Rounding `gap` to the nearest number first would swap two numbers'
 * type in a single frame at the halfway point of every scroll, and the column would
 * appear to jump from one value to the next rather than roll between them.
 */
export function typeAt(gap: number): { size: number; weight: number } {
  const away = Math.min(Math.abs(gap), 2);
  const [from, to] = away < 1 ? [26, 19] : [19, 15];
  return {
    size: from + (to - from) * (away - (away < 1 ? 0 : 1)),
    weight: 800 - 200 * Math.min(away, 1),
  };
}
